import { useEffect, useRef, useState } from 'react';
import { DororaidIcon } from '../../../shared/ui/DororaidIcon.jsx';
import { useNikkeCatalog } from '../../nikke-catalog/hooks/useNikkeCatalog.js';
import { RaidPracticeDeckPicker } from '../../raid-practice/shared/RaidPracticeDeckPicker.jsx';
import {
  RECOMMENDED_DECK_ELEMENTS,
  validateRecommendedDeck,
} from '../domain/recommendedDecks.js';

function initialForm(deck) {
  return {
    title: String(deck?.title || ''),
    element: RECOMMENDED_DECK_ELEMENTS.includes(deck?.element) ? deck.element : RECOMMENDED_DECK_ELEMENTS[0],
    deck: Array.from({ length: 5 }, (_, index) => String(deck?.deck?.[index] || '')),
    note: String(deck?.note || ''),
  };
}

export function RecommendedDeckEditor({ deck = null, mobile = false, working = '', onSave, onClose }) {
  const { catalog } = useNikkeCatalog();
  const [form, setForm] = useState(() => initialForm(deck));
  const [errorMessage, setErrorMessage] = useState('');
  const overlayRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    const body = document.body;
    const previousOverflow = body.style.overflow;
    const previousOverscrollBehavior = body.style.overscrollBehavior;
    body.style.overflow = 'hidden';
    body.style.overscrollBehavior = 'none';

    let viewportFrame = 0;
    let firstSettleTimer = 0;
    let finalSettleTimer = 0;
    const visualViewport = window.visualViewport;

    const syncViewport = () => {
      window.cancelAnimationFrame(viewportFrame);
      viewportFrame = window.requestAnimationFrame(() => {
        const viewportHeight = Math.max(
          1,
          Math.round(visualViewport?.height || window.innerHeight || document.documentElement.clientHeight || 1),
        );
        const viewportTop = Math.max(0, Math.round(visualViewport?.offsetTop || 0));
        const overlay = overlayRef.current;
        overlay?.style.setProperty('--recommended-viewport-height', `${viewportHeight}px`);
        overlay?.style.setProperty('--recommended-viewport-top', `${viewportTop}px`);

        const scrollArea = scrollRef.current;
        if (scrollArea) {
          const maxScrollTop = Math.max(0, scrollArea.scrollHeight - scrollArea.clientHeight);
          if (scrollArea.scrollTop > maxScrollTop) scrollArea.scrollTop = maxScrollTop;
        }
      });
    };

    const settleViewport = () => {
      syncViewport();
      window.clearTimeout(firstSettleTimer);
      window.clearTimeout(finalSettleTimer);
      firstSettleTimer = window.setTimeout(syncViewport, 90);
      finalSettleTimer = window.setTimeout(syncViewport, 320);
    };

    const onKeyDown = (event) => { if (event.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', settleViewport, { passive: true });
    window.addEventListener('orientationchange', settleViewport, { passive: true });
    visualViewport?.addEventListener('resize', settleViewport, { passive: true });
    visualViewport?.addEventListener('scroll', syncViewport, { passive: true });
    syncViewport();

    return () => {
      body.style.overflow = previousOverflow;
      body.style.overscrollBehavior = previousOverscrollBehavior;
      window.cancelAnimationFrame(viewportFrame);
      window.clearTimeout(firstSettleTimer);
      window.clearTimeout(finalSettleTimer);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', settleViewport);
      window.removeEventListener('orientationchange', settleViewport);
      visualViewport?.removeEventListener('resize', settleViewport);
      visualViewport?.removeEventListener('scroll', syncViewport);
    };
  }, [onClose]);

  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const submit = async () => {
    const validated = validateRecommendedDeck(form, catalog);
    if (!validated.ok) {
      setErrorMessage(validated.message);
      return;
    }
    setErrorMessage('');
    const ok = await onSave?.(validated.value, deck?.id || '');
    if (ok) onClose?.();
  };

  const saveLabel = working ? '저장 중…' : '저장';

  return (
    <div ref={overlayRef} className={`recommended-deck-editor-overlay ${mobile ? 'mobile' : 'desktop'}`} role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose?.(); }}>
      <section className="recommended-deck-editor" role="dialog" aria-modal="true" aria-label={deck ? '추천덱 수정' : '추천덱 등록'}>
        <header className="recommended-deck-editor-head">
          <div><span>RECOMMENDED SQUAD</span><h2>{deck ? '추천덱 수정' : '새 추천덱 등록'}</h2></div>
          <div>
            <button type="button" className="ui-icon-action save" disabled={Boolean(working)} onClick={submit} aria-label={saveLabel} title={saveLabel}><DororaidIcon name="save" size={20} /></button>
            <button type="button" className="ui-icon-action close" onClick={onClose} aria-label="닫기" title="닫기"><DororaidIcon name="close" size={20} /></button>
          </div>
        </header>
        <div ref={scrollRef} className="recommended-deck-editor-scroll">
          <section className="recommended-deck-editor-form">
            <label><span>추천덱 이름</span><input maxLength={100} value={form.title} onChange={(event) => update('title', event.target.value)} placeholder="작열 기본덱 / 저점 대체덱" /></label>
            <label><span>추천 속성</span><select value={form.element} onChange={(event) => update('element', event.target.value)}>{RECOMMENDED_DECK_ELEMENTS.map((element) => <option value={element} key={element}>{element}</option>)}</select></label>
          </section>
          <section className="recommended-deck-editor-squad">
            <header className="recommended-deck-editor-squad-head"><span>SQUAD SET</span><strong>니케 5명</strong></header>
            <section className="recommended-deck-editor-picker">
              <RaidPracticeDeckPicker value={form.deck} onChange={(value) => update('deck', value)} mobile={mobile} />
            </section>
          </section>
          <label className="recommended-deck-editor-note"><span>메모</span><textarea maxLength={500} value={form.note} onChange={(event) => update('note', event.target.value)} placeholder="사용 조건, 대체 캐릭터, 운영 주의사항" /></label>
          {errorMessage ? <p className="recommended-deck-editor-error">{errorMessage}</p> : null}
        </div>
      </section>
    </div>
  );
}
